import 'lazysizes';
import { createApp } from 'vue';
import { createStore } from 'vuex';

import { hoistJsonScripts } from '@Foundation/utilities';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import Foundation from '@Foundation';
import LoadScriptModule from '@Foundation/loadscript';
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { tag } from '@Foundation/analyticsHandler/directives/tag';

// Feature
import AccessibilityStore from '@Foundation/accessibilityStore';
import AlertModule from '@Feature/Alert';
import ArticleShareItem from '@Feature/PageContentExt';
import BazaarVoiceModule from '@Feature/Bazaarvoice';
import BeautyMagModule from '@Feature/BeautyMag';
import CarouselModule from '@Feature/Carousel';
import CookieBanner from '@Feature/PageContentExt';
import FilterModule from '@Feature/Filter';
import GenericForm from '@Feature/GenericForm';
import HeaderLogo from '@Feature/HeaderLogo';
import Iframepopup from '@Feature/IframePopup';
import InstagramComponent from '@Feature/Instagram';
import Likebutton from '@Feature/LikeButton';
import LiveChat from '@Feature/LiveChat';
import ModalModule from '@Feature/Modal';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import NotificationModule from '@Feature/Notification';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBadge from '@Feature/OapBadge';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapBeautyProfile from '@Feature/OapBeautyProfile';
import OapCard from '@Feature/OapCard';
import OapCardPopin from '@Feature/OapCardPopin';
import OapCarouselControls from '@Feature/CarouselControls';
import OapChannelAdvisor from '@Feature/OapChannelAdvisor';
import OapCountdown from '@Feature/OapCountdown';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapDsfAppVtoWrapper from '@Feature/OapDsfAppVtoWrapper';
import OapDsfAppWrapper from '@Feature/OapDsfAppWrapper';
import OapEventsNavigation from '@Feature/OapEventsNavigation';
import OapFavorite from '@Feature/OapFavorite';
import OapFloatingIsland from '@Feature/OapFloatingIsland';
import OapFocusTrap from '@Feature/OapFocusTrap';
import OapGeolocationPopin from '@Feature/OapGeolocationPopin';
import OapIframe from '@Feature/OapIframe';
import OapLoader from '@Feature/OapLoader';
import OapMediaCarousel from '@Feature/OapMediaCarousel';
import OapMediaCover from '@Feature/OapMediaCover';
import OapPopin from '@Feature/OapPopin';
import OapPopup from '@Feature/OapPopup';
import OapProductVariantSelector from '@Feature/ProductVariantSelector';
import OapPromoBar from '@Feature/OapPromoBar';
import OapQuiz from '@Feature/Questionnaire';
import OapRating from '@Feature/OapRating';
import OapRoutineBuilder from '@Feature/RoutineBuilder';
import OapScroller from '@Feature/OapScroller';
import OapSkindrAppWrapper from '@Feature/OapSkindrAppWrapper';
import OapSlider from '@Feature/OapSlider';
import OapStories from '@Feature/OapStories';
import OapStrikethroughScrolling from '@Feature/OapStrikethroughScrolling';
import OapTabComponent from '@Feature/OapTab';
import OapTableOfContents from '@Feature/OapTableOfContents';
import OapToastNotification from '@Feature/OapToastNotification';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import ProductModule from '@Feature/ProductModule';
import RealDialog from '@Feature/RealDialog';
import RevTrax from '@Feature/RevTrax';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Sfmc from '@Feature/SFMC';
import Slider from '@Feature/Slider';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import VirtualTryOn from '@Feature/VirtualTryOn';
import VueModels from '@Feature/VueModels';
import OapPageNavigation from '@Feature/OapPageNavigation';
import OapPromoCard from '@Feature/OapPromoCard';
import FooterNewsletterSignupModal from '@Feature/FooterNewsletterSignupModal';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapBeforeAfter from '@Feature/OapBeforeAfter';

// 3rd party
import { Tabs, Tab } from 'vue3-tabs-component';
import ToastedPlugin from '@Feature/OapToastNotification/code/plugin';

const VueTabsPlugin = {
  install(app) {
    app.component('Tabs', Tabs);
    app.component('Tab', Tab);
  },
};

exposeGlobals();

const app = createApp({});
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

// Bootstrap Vue
app.use(ToastedPlugin);
app.use(VueTabsPlugin);

// Register directives
Object.entries({
  ...Foundation.directives,
  ...LoadScriptModule.directives,
  ...NavigationModule.directives,
  ...Search.directives,
  tag,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register components
Object.entries({
  ...AlertModule.components,
  ...ArticleShareItem.components,
  ...BazaarVoiceModule.components,
  ...BeautyMagModule.components,
  ...CarouselModule.components,
  ...CookieBanner.components,
  ...FilterModule.components,
  ...Foundation.components,
  ...GenericForm.components,
  ...HeaderLogo.components,
  ...Iframepopup.components,
  ...InstagramComponent.components,
  ...Likebutton.components,
  ...LiveChat.components,
  ...LoadScriptModule.components,
  ...ModalModule.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...NotificationModule.components,
  ...OapAccountNavigation.components,
  ...OapBadge.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapBeautyProfile.components,
  ...OapCard.components,
  ...OapCardPopin.components,
  ...OapCarouselControls.components,
  ...OapChannelAdvisor.components,
  ...OapCountdown.components,
  ...OapEventfulTextInput.components,
  ...OapDsfAppVtoWrapper.components,
  ...OapDsfAppWrapper.components,
  ...OapEventsNavigation.components,
  ...OapFavorite.components,
  ...OapFloatingIsland.components,
  ...OapFocusTrap.components,
  ...OapGeolocationPopin.components,
  ...OapIframe.components,
  ...OapLoader.components,
  ...OapMediaCarousel.components,
  ...OapMediaCover.components,
  ...OapPopin.components,
  ...OapPopup.components,
  ...OapProductVariantSelector.components,
  ...OapPromoBar.components,
  ...OapQuiz.components,
  ...OapRating.components,
  ...OapRoutineBuilder.components,
  ...OapTableOfContents.components,
  ...OapScroller.components,
  ...OapSkindrAppWrapper.components,
  ...OapSlider.components,
  ...OapStories.components,
  ...OapStrikethroughScrolling.components,
  ...OapTabComponent.components,
  ...OapToastNotification.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...ProductModule.components,
  ...RealDialog.components,
  ...RevTrax.components,
  ...SearchSuggestions.components,
  ...Sfmc.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...VirtualTryOn.components,
  ...VueModels.components,
  ...OapPageNavigation.components,
  ...OapPromoCard.components,
  ...FooterNewsletterSignupModal.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...FloatingSidebar.components,
  ...OapBeforeAfter.components,
  ...WishList.components,
}).forEach(([name, component]) => app.component(name, component));

Object.entries({
  ...Foundation.filters,
}).forEach(([name, filter]) => app.filter(name, filter));

const store = createStore({
  modules: {
    ...AccessibilityStore.modules,
    ...OapBeautyProfile.modules,
    ...OapFavorite.modules,
    ...OapTabComponent.modules,
    ...ProductModule.modules,
    ...VirtualTryOn.modules,
  },
});

app.use(store);

/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, {});
// Initialize third party loader
init();

requireAll(require.context('../../../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);

window.RefAppMctb && app.use(window.RefAppMctb);
window.RefAppAmp && app.use(window.RefAppAmp);
window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);
window.RefAppFaq && app.use(window.RefAppFaq);
window.RefAppRatingsReviews && app.use(window.RefAppRatingsReviews);
window.RefAppBackToTop && app.use(window.RefAppBackToTop);
window.RefAppUserRedirect && app.use(window.RefAppUserRedirect);
globalThis.RefAppBeforeAfter && app.use(globalThis.RefAppBeforeAfter);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
